import { authWrite, getToken } from '@/lib/auth';
import { getEntries } from '@/data/journal';
import { getPlans } from '@/data/gardenPlanner';
import type { JournalEntry } from '@/data/journal';
import type { GardenPlan } from '@/data/gardenPlanner';
import type { PlantingLog } from '@/lib/plantingTracker';
import type { HarvestEntry } from '@/lib/harvestLog';
import type { SeedPacket } from '@/lib/seedInventory';
import type { UserGardenLocation } from '@/lib/userGardenLocation';
import { normalizeUserGardenLocation } from '@/lib/userGardenLocation';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CustomTask {
  id: string;
  text: string;
  checked: boolean;
  dueDate?: string;    // ISO date string, optional
  archivedAt?: string; // ISO timestamp — set when task is archived after completion
  isReminder?: boolean; // true for "waiting for conditions" reminder tasks
}

export interface TaskData {
  checked: number[];
  custom: CustomTask[];
  autoChecked?: string[];
  autoDismissed?: string[];
  autoOverrides?: Record<string, string>;
  autoArchived?: Record<string, string>;
}

export interface CombinedTaskData {
  checked: number[];
  custom: CustomTask[];
  autoChecked: string[];
  autoDismissed: string[];
  autoOverrides: Record<string, string>;
  autoArchived: Record<string, string>;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function authHeaders(): Record<string, string> {
  const token = getToken();
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) h['Authorization'] = `Bearer ${token}`;
  return h;
}

// ── Safety guard ──────────────────────────────────────────────────────────────

function safeToWrite(newData: unknown[], existingData: unknown[]): boolean {
  if (newData.length === 0 && existingData.length > 0) {
    console.warn('[dataSync] Prevented writing empty array over existing data');
    return false;
  }
  return true;
}

function readLocalJSON<T>(key: string): T | null | undefined {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function isObjectRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asNumberArray(value: unknown): number[] | undefined {
  return Array.isArray(value) && value.every(item => typeof item === 'number')
    ? value
    : undefined;
}

function asStringArray(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
    ? value
    : undefined;
}

function asCustomTaskArray(value: unknown): CustomTask[] | undefined {
  return Array.isArray(value) ? (value as CustomTask[]) : undefined;
}

function asStringRecord(value: unknown): Record<string, string> | undefined {
  if (!isObjectRecord(value)) return undefined;
  return Object.values(value).every(item => typeof item === 'string')
    ? (value as Record<string, string>)
    : undefined;
}

function isEmptyJournalEntries(entries: JournalEntry[]): boolean {
  return entries.length === 0;
}

function isEmptyGardenPlans(plans: GardenPlan[]): boolean {
  return plans.length === 0;
}

function isEmptyPlantingLogs(logs: PlantingLog[]): boolean {
  return logs.length === 0;
}

function isEmptyHarvestEntries(entries: HarvestEntry[]): boolean {
  return entries.length === 0;
}

function isEmptySeedInventory(packets: SeedPacket[]): boolean {
  return packets.length === 0;
}

function isEmptyFavorites(ids: string[]): boolean {
  return ids.length === 0;
}

function normalizeStoredTasks(value: unknown): CombinedTaskData {
  const data = isObjectRecord(value) ? value : {};
  return normalizeCombined({
    checked: asNumberArray(data.checked),
    custom: asCustomTaskArray(data.custom),
    autoChecked: asStringArray(data.autoChecked),
    autoDismissed: asStringArray(data.autoDismissed),
    autoOverrides: asStringRecord(data.autoOverrides),
    autoArchived: asStringRecord(data.autoArchived),
  });
}

function isEmptyTaskState(tasks: CombinedTaskData): boolean {
  return (
    tasks.checked.length === 0 &&
    tasks.custom.length === 0 &&
    tasks.autoChecked.length === 0 &&
    tasks.autoDismissed.length === 0 &&
    Object.keys(tasks.autoOverrides).length === 0 &&
    Object.keys(tasks.autoArchived).length === 0
  );
}

interface LocalZoneData {
  zone: number | null;
  zip: string | null;
  gardenLocation: UserGardenLocation | null;
}

function readLocalZoneData(): LocalZoneData | null {
  if (typeof window === 'undefined') return null;

  let zone: number | null = null;
  let zip: string | null = null;
  let gardenLocation: UserGardenLocation | null = null;

  const zoneData = readLocalJSON<{ zone?: unknown; zip?: unknown; gardenLocation?: unknown; location?: unknown }>('garden-user-zone');
  if (zoneData === undefined) return null;
  if (zoneData && typeof zoneData.zone === 'number' && zoneData.zone >= 1 && zoneData.zone <= 13) {
    zone = zoneData.zone;
  }
  if (zoneData && typeof zoneData.zip === 'string' && zoneData.zip.trim()) {
    zip = zoneData.zip.trim();
  }
  if (zoneData) {
    gardenLocation = normalizeUserGardenLocation(zoneData.gardenLocation ?? zoneData.location);
  }

  const dedicatedLocation = readLocalJSON<unknown>('garden-user-location');
  if (dedicatedLocation !== undefined) {
    gardenLocation = normalizeUserGardenLocation(dedicatedLocation) ?? gardenLocation;
  }

  const dedicatedZip = localStorage.getItem('garden-zone-zip');
  if (typeof dedicatedZip === 'string' && dedicatedZip.trim()) {
    zip = dedicatedZip.trim();
  }

  if (zone === null && !zip) return null;
  return { zone, zip, gardenLocation };
}

function isEmptyZoneData(data: { zone?: number | null; zip?: string | null; gardenLocation?: UserGardenLocation | null }): boolean {
  return data.zone == null && !(typeof data.zip === 'string' && data.zip.trim()) && !data.gardenLocation;
}

// ── Journal ───────────────────────────────────────────────────────────────────

export async function getJournalEntries(): Promise<JournalEntry[]> {
  if (!getToken()) return [];
  try {
    const res = await fetch('/api/user/journal', { headers: authHeaders() });
    if (!res.ok) return getEntries();
    const data = await res.json() as { entries?: JournalEntry[] };
    if (!Array.isArray(data.entries)) return getEntries();
    // Cache KV result locally for offline fallback (logged-in users only)
    try { localStorage.setItem('garden-journal-entries', JSON.stringify(data.entries)); } catch {}
    return data.entries;
  } catch {
    return getEntries();
  }
}

export async function saveJournalEntries(entries: JournalEntry[]): Promise<void> {
  try { localStorage.setItem('garden-journal-entries', JSON.stringify(entries)); } catch {}
  if (!getToken()) return;

  // Guard: never overwrite non-empty KV data with an empty array
  const existingCache = (() => {
    try {
      const raw = localStorage.getItem('garden-journal-entries-prev');
      return raw ? (JSON.parse(raw) as unknown[]) : [];
    } catch { return []; }
  })();
  if (!safeToWrite(entries, existingCache)) return;

  try {
    await authWrite(
      '/api/user/journal',
      {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ entries }),
      },
      'Your journal changes were saved locally, but could not be synced.',
    );
    try { localStorage.setItem('garden-journal-entries-prev', JSON.stringify(entries)); } catch {}
  } catch {}
}

// ── Garden Plans ──────────────────────────────────────────────────────────────

export async function getGardenPlans(): Promise<GardenPlan[]> {
  if (!getToken()) return [];
  try {
    const res = await fetch('/api/user/gardens', { headers: authHeaders() });
    if (!res.ok) return getPlans();
    const data = await res.json() as { plans?: GardenPlan[] };
    if (!Array.isArray(data.plans)) return getPlans();
    try { localStorage.setItem('garden-plans', JSON.stringify(data.plans)); } catch {}
    return data.plans;
  } catch {
    return getPlans();
  }
}

/** Immediately writes to localStorage and awaits the KV push. Use before navigation. */
export async function saveGardenPlans(plans: GardenPlan[]): Promise<void> {
  try { localStorage.setItem('garden-plans', JSON.stringify(plans)); } catch {}
  if (!getToken()) return;
  try {
    await fetch('/api/user/gardens', {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ plans }),
    });
  } catch {}
}

let _gardenSaveTimer: ReturnType<typeof setTimeout> | null = null;

export function cancelPendingGardenPlanSave(): void {
  if (_gardenSaveTimer) {
    clearTimeout(_gardenSaveTimer);
    _gardenSaveTimer = null;
  }
}

/** Immediately writes to localStorage; debounces the KV push to avoid excessive API calls. */
export function saveGardenPlansDebounced(plans: GardenPlan[]): void {
  try { localStorage.setItem('garden-plans', JSON.stringify(plans)); } catch {}
  cancelPendingGardenPlanSave();
  const scheduledToken = getToken();
  _gardenSaveTimer = setTimeout(async () => {
    const currentToken = getToken();
    if (!scheduledToken || !currentToken || currentToken !== scheduledToken) return;
    try {
      await fetch('/api/user/gardens', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ plans }),
      });
    } catch {}
    finally {
      _gardenSaveTimer = null;
    }
  }, 2000);
}

// ── Tasks ─────────────────────────────────────────────────────────────────────

const TASKS_LOCAL_KEY = 'garden-tasks';

// Module-level cache shared between all task functions.
// Populated on first GET; ensures separate save calls don't clobber each other.
// Also populated by UserDataContext via primeTaskCache() for instant access.
let _taskCache: CombinedTaskData | null = null;

/** Prime the module-level cache from externally loaded data (called by UserDataContext). */
export function primeTaskCache(data: Partial<CombinedTaskData>): void {
  _taskCache = normalizeCombined(data);
}

export function resetTaskCache(): void {
  _taskCache = null;
}

function emptyTaskData(): CombinedTaskData {
  return { checked: [], custom: [], autoChecked: [], autoDismissed: [], autoOverrides: {}, autoArchived: {} };
}

function normalizeCombined(d: Partial<CombinedTaskData>): CombinedTaskData {
  return {
    checked:       Array.isArray(d.checked)       ? d.checked       : [],
    custom:        Array.isArray(d.custom)         ? d.custom        : [],
    autoChecked:   Array.isArray(d.autoChecked)   ? d.autoChecked   : [],
    autoDismissed: Array.isArray(d.autoDismissed) ? d.autoDismissed : [],
    autoOverrides: (d.autoOverrides && typeof d.autoOverrides === 'object' && !Array.isArray(d.autoOverrides))
      ? d.autoOverrides as Record<string, string> : {},
    autoArchived:  (d.autoArchived  && typeof d.autoArchived  === 'object' && !Array.isArray(d.autoArchived))
      ? d.autoArchived  as Record<string, string> : {},
  };
}

function loadLocalCombined(): CombinedTaskData {
  try {
    const raw = localStorage.getItem(TASKS_LOCAL_KEY);
    if (raw) return normalizeCombined(JSON.parse(raw) as Partial<CombinedTaskData>);
  } catch {}
  return emptyTaskData();
}

async function getFullTaskState(): Promise<CombinedTaskData> {
  if (!getToken()) {
    const local = loadLocalCombined();
    _taskCache = local;
    return local;
  }
  if (_taskCache) return _taskCache;
  const fallback = loadLocalCombined();
  try {
    const res = await fetch('/api/user/tasks', { headers: authHeaders() });
    if (!res.ok) { _taskCache = fallback; return fallback; }
    const data = await res.json() as Partial<CombinedTaskData>;
    const state = normalizeCombined(data);
    _taskCache = state;
    try { localStorage.setItem(TASKS_LOCAL_KEY, JSON.stringify(state)); } catch {}
    return state;
  } catch {
    _taskCache = fallback;
    return fallback;
  }
}

async function putFullTaskState(partial: Partial<CombinedTaskData>): Promise<void> {
  const base = _taskCache ?? emptyTaskData();
  const next: CombinedTaskData = { ...base, ...partial };
  _taskCache = next;
  try { localStorage.setItem(TASKS_LOCAL_KEY, JSON.stringify(next)); } catch {}
  if (!getToken()) return;
  try {
    await authWrite(
      '/api/user/tasks',
      {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(next),
      },
      'Your task changes were saved locally, but could not be synced.',
    );
  } catch {}
}

export async function getTasks(): Promise<TaskData> {
  const s = await getFullTaskState();
  return { checked: s.checked, custom: s.custom };
}

export async function saveTasks(data: TaskData): Promise<void> {
  await putFullTaskState(data);
}

// ── Auto-task state ───────────────────────────────────────────────────────────

export interface AutoTaskState {
  autoChecked: string[];
  autoDismissed: string[];
  autoOverrides: Record<string, string>; // taskId → overridden ISO due date
  autoArchived: Record<string, string>;  // taskId → ISO timestamp of archival
}

export async function getAutoTaskState(): Promise<AutoTaskState> {
  const s = await getFullTaskState();
  return {
    autoChecked:   s.autoChecked,
    autoDismissed: s.autoDismissed,
    autoOverrides: s.autoOverrides,
    autoArchived:  s.autoArchived,
  };
}

export async function saveAutoTaskState(state: AutoTaskState): Promise<void> {
  await putFullTaskState(state);
}

// ── Migration ─────────────────────────────────────────────────────────────────

/** Push any existing localStorage data to KV if KV is empty — call once on login. */
export async function migrateLocalStorageToKV(): Promise<void> {
  if (!getToken()) return;
  await Promise.allSettled([
    (async () => {
      try {
        const res = await fetch('/api/user/journal', { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json() as { entries?: JournalEntry[] };
        if (Array.isArray(data.entries) && isEmptyJournalEntries(data.entries)) {
          const local = getEntries();
          if (!isEmptyJournalEntries(local)) await saveJournalEntries(local);
        }
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/gardens', { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json() as { plans?: GardenPlan[] };
        if (Array.isArray(data.plans) && data.plans.length === 0) {
          const local = getPlans();
          if (!isEmptyGardenPlans(local)) await authWrite(
            '/api/user/gardens',
            {
              method: 'PUT',
              headers: authHeaders(),
              body: JSON.stringify({ plans: local }),
            },
            'Your garden plans were saved locally, but could not be synced.',
          );
        }
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/tasks', { headers: authHeaders() });
        if (!res.ok) return;
        const serverTasks = normalizeStoredTasks(await res.json() as unknown);
        if (!isEmptyTaskState(serverTasks)) return;

        const localRaw = readLocalJSON<unknown>(TASKS_LOCAL_KEY);
        if (localRaw === undefined) return;
        const localTasks = normalizeStoredTasks(localRaw);
        if (isEmptyTaskState(localTasks)) return;

        await authWrite(
          '/api/user/tasks',
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify(localTasks),
          },
          'Your task changes were saved locally, but could not be synced.',
        );
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/seeds', { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json() as { packets?: SeedPacket[] };
        if (!Array.isArray(data.packets) || !isEmptySeedInventory(data.packets)) return;

        const local = readLocalJSON<SeedPacket[]>('garden-seed-inventory');
        if (local === undefined || !Array.isArray(local) || isEmptySeedInventory(local)) return;

        await authWrite(
          '/api/user/seeds',
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ packets: local }),
          },
          'Your seed inventory changes were saved locally, but could not be synced.',
        );
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/planting-logs', { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json() as { logs?: PlantingLog[] };
        if (!Array.isArray(data.logs) || !isEmptyPlantingLogs(data.logs)) return;

        const local = readLocalJSON<PlantingLog[]>('garden-planting-logs');
        if (local === undefined || !Array.isArray(local) || isEmptyPlantingLogs(local)) return;

        await authWrite(
          '/api/user/planting-logs',
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ logs: local }),
          },
          'Your plant log changes were saved locally, but could not be synced.',
        );
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/harvest', { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json() as { entries?: HarvestEntry[] };
        if (!Array.isArray(data.entries) || !isEmptyHarvestEntries(data.entries)) return;

        const local = readLocalJSON<HarvestEntry[]>('garden-harvest-log');
        if (local === undefined || !Array.isArray(local) || isEmptyHarvestEntries(local)) return;

        await authWrite(
          '/api/user/harvest',
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ entries: local }),
          },
          'Your harvest changes were saved locally, but could not be synced.',
        );
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/saved-plants', { headers: authHeaders() });
        if (!res.ok) return;
        const data = await res.json() as { plantIds?: string[] };
        if (!Array.isArray(data.plantIds) || !isEmptyFavorites(data.plantIds)) return;

        const local = readLocalJSON<string[]>('garden_favorites');
        if (local === undefined || !Array.isArray(local) || isEmptyFavorites(local)) return;

        await authWrite(
          '/api/user/saved-plants',
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({ plantIds: local }),
          },
          'Your favorites were saved locally, but could not be synced.',
        );
      } catch {}
    })(),
    (async () => {
      try {
        const res = await fetch('/api/user/zone', { headers: authHeaders() });
        if (!res.ok) return;
        const server = await res.json() as { zone?: number | null; zip?: string | null; gardenLocation?: UserGardenLocation | null };
        if (!isEmptyZoneData(server)) return;

        const local = readLocalZoneData();
        if (!local || isEmptyZoneData(local)) return;

        await authWrite(
          '/api/user/zone',
          {
            method: 'PUT',
            headers: authHeaders(),
            body: JSON.stringify({
              ...(local.zone !== null ? { zone: local.zone } : {}),
              ...(local.zip ? { zip: local.zip } : {}),
              gardenLocation: local.gardenLocation,
            }),
          },
          'Your growing zone could not be synced.',
        );
      } catch {}
    })(),
  ]);
}
